import { useEffect, useState } from "react";
import type { InstagramAccount } from "../types";

type PageScopedInstagramFetch = (
  facebookPageId: string
) => void | Promise<InstagramAccount[] | void>;

type LoadedPageAccounts = {
  pageId: string;
  accounts: InstagramAccount[];
};

/** Loads Instagram accounts for the selected Facebook page while a surface is open. */
export function usePageScopedInstagramAccounts(
  isOpen: boolean,
  facebookPageId: string | undefined,
  fetchAccounts?: PageScopedInstagramFetch
): InstagramAccount[] {
  const pageId = facebookPageId?.trim() ?? "";
  const [loaded, setLoaded] = useState<LoadedPageAccounts>({
    pageId: "",
    accounts: [],
  });

  useEffect(() => {
    if (!isOpen) {
      setLoaded({ pageId: "", accounts: [] });
      return;
    }
    if (!pageId || !fetchAccounts) {
      setLoaded({ pageId: "", accounts: [] });
      return;
    }

    let cancelled = false;
    void Promise.resolve(fetchAccounts(pageId)).then((rows) => {
      if (!cancelled) {
        setLoaded({
          pageId,
          accounts: Array.isArray(rows) ? rows : [],
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fetchAccounts, isOpen, pageId]);

  if (!isOpen || !pageId || !fetchAccounts) {
    return [];
  }

  // Drop rows from the previous page synchronously while the new fetch is in flight.
  return loaded.pageId === pageId ? loaded.accounts : [];
}